/**
 * The Ready · Set · Go! pulse maths the Learn exercises share: Rhythm Echo's cue (issue #3519) and
 * Sing in Solfège's count-in (issue #3722). It began in Rhythm Echo and moved here when the second
 * exercise took it up, unchanged.
 *
 * Three silent words pulse in time with the tempo: "Ready", then "Set" on the next beat, then "Go!"
 * on the beat where the singer comes in. Each word pulses once, and nothing is shown between
 * pulses. Each exercise says where the three beats fall: in Rhythm Echo, between the pattern and
 * the singer's taps; in Sing in Solfège, on the count-in's last two beats and the take's first.
 *
 * The pulse is driven by the audio clock rather than by CSS keyframes, so a story or a test can
 * render any instant deterministically: everything here is a pure function of `nowMs`. The curve is
 * punchy — a quick run up so the word is fully opaque *exactly* on the beat, a brief dwell at the
 * top, then a quick run down. The cubic gives the rounded top (the dwell) with steep sides; the
 * scale follows the opacity so the word grows in as it fades in and shrinks out as it fades out.
 *
 * Pure: no React, no DOM.
 */

import { ResultColor } from '../../../../common/src/rhythm_echo.ts';
import { resultColorCss } from './learn_colors.ts';

export type CueWord = 'ready' | 'set' | 'go';

export const cueWords: CueWord[] = ['ready', 'set', 'go'];

export const cueText: Record<CueWord, string> = {
  ready: 'Ready',
  set: 'Set',
  go: 'Go!',
};

// The result colours double as the traffic light: red, yellow, green (see learn_colors.ts)
export const cueColor: Record<CueWord, ResultColor> = {
  ready: 'red',
  set: 'yellow',
  go: 'green',
};

export const cueColorCss: Record<CueWord, string> = {
  ready: resultColorCss[cueColor.ready],
  set: resultColorCss[cueColor.set],
  go: resultColorCss[cueColor.go],
};

// The tunable constants, all in one place. Rise + fall is half a beat, so two windows a beat apart
// never overlap and at most one word is ever visible (see visibleCue).
export const cueRiseFraction = 0.15; // of a beat, before the word's beat
export const cueFallFraction = 0.35; // of a beat, after it
export const cueMinScale = 0.7; // the scale at the edges of the window, growing to 1 on the beat

export interface CuePulse {
  opacity: number;
  scale: number;
}

// The pulse for one word at `nowMs`, or undefined when its window does not contain `nowMs`.
export function cuePulse(nowMs: number, wordBeatMs: number, beatMs: number): CuePulse | undefined {
  const riseMs = cueRiseFraction * beatMs;
  const fallMs = cueFallFraction * beatMs;
  const t = nowMs - wordBeatMs;
  if (t < -riseMs || t >= fallMs) return undefined;
  const span = t < 0 ? riseMs : fallMs;
  const opacity = clamp01(1 - Math.abs(t / span) ** 3);
  return { opacity, scale: cueMinScale + (1 - cueMinScale) * opacity };
}

export interface VisibleCue extends CuePulse {
  word: CueWord;
}

// The one word visible at `nowMs`, if any. The windows are a beat apart and half a beat wide, so
// they cannot overlap; the first match is the only match.
export function visibleCue(nowMs: number, cueBeatsMs: Record<CueWord, number>, beatMs: number): VisibleCue | undefined {
  for (const word of cueWords) {
    const pulse = cuePulse(nowMs, cueBeatsMs[word], beatMs);
    if (pulse) return { word, ...pulse };
  }
  return undefined;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}
